/**
 * Phase A R6 B9 — Share Audio pre-share modal.
 *
 * Surfaces a one-time confirmation BEFORE we call getDisplayMedia so DJs
 * understand:
 *   (1) the OS picker is coming and they must check "Share audio";
 *   (2) Full Spectrum Center never receives or shares their screen content — audio
 *       only.
 *
 * Persistence: when the user ticks "Don't show again" + Continue, we set
 * `localStorage["sr-share-audio-tooltip-dismissed"] = "1"`. Subsequent
 * Share Audio toggles skip the modal and call getDisplayMedia directly.
 *
 * Required testids (per spec):
 *   - share-audio-pre-modal
 *   - share-audio-pre-cancel
 *   - share-audio-pre-continue
 *   - share-audio-pre-dont-show-again
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

export const SHARE_AUDIO_DISMISS_KEY = "sr-share-audio-tooltip-dismissed";

export function shareAudioModalDismissed() {
  try { return localStorage.getItem(SHARE_AUDIO_DISMISS_KEY) === "1"; }
  catch { return false; }
}

export default function ShareAudioPreModal({ open, onCancel, onContinue }) {
  const [dontShow, setDontShow] = useState(false);

  const handleContinue = () => {
    if (dontShow) {
      try { localStorage.setItem(SHARE_AUDIO_DISMISS_KEY, "1"); } catch { /* */ }
    }
    onContinue?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent
        className="bg-[#16161d] border-white/10 text-white max-w-md"
        data-testid="share-audio-pre-modal">
        <DialogTitle className="text-base font-semibold">About to share audio</DialogTitle>
        <DialogDescription asChild>
          <div className="text-[13px] text-white/75 space-y-3 mt-2">
            <p>
              Your browser will open a picker. Choose any tab or window with sound
              playing — Chrome will offer a <span className="font-mono text-white">Share audio</span> checkbox; make
              sure it's ON.
            </p>
            <p className="text-emerald-300/90">
              Full Spectrum Center only sends the audio. We never receive or share your screen content.
            </p>
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-[11px] text-cyan-200/90 space-y-1 mb-2">
              <div className="font-bold uppercase tracking-[0.1em] text-cyan-300 text-[10px]">Recommended</div>
              <p>For the best DJ experience, use the <span className="font-bold text-white">Full Spectrum Center desktop app</span>. It captures system audio on all platforms without browser limitations.</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-[11px] text-amber-200/90 space-y-1">
              <div className="font-bold uppercase tracking-[0.1em] text-amber-300 text-[10px]">Browser limitations</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li><span className="font-semibold">Chrome/Edge (Windows):</span> full system audio capture works.</li>
                <li><span className="font-semibold">Chrome/Edge (Mac):</span> only tab audio. Use BlackHole for DJ software.</li>
                <li><span className="font-semibold">Safari:</span> not supported.</li>
                <li><span className="font-semibold">Firefox:</span> tab audio only, no system audio.</li>
                <li><span className="font-semibold">Mobile (iOS/Android):</span> not available. Use the desktop app.</li>
              </ul>
            </div>
          </div>
        </DialogDescription>

        <label className="flex items-center gap-2 mt-4 text-[12px] text-white/70 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="accent-emerald-500"
            data-testid="share-audio-pre-dont-show-again"
          />
          Don't show again
        </label>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => onCancel?.()}
            className="px-3 py-2 rounded-md text-xs font-semibold bg-white/5 hover:bg-white/10 text-white/80 border border-white/10"
            data-testid="share-audio-pre-cancel">
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className="px-3 py-2 rounded-md text-xs font-bold uppercase tracking-[0.08em] text-black"
            style={{ background: "linear-gradient(135deg, #a855f7 0%, #34d399 100%)" }}
            data-testid="share-audio-pre-continue">
            Continue
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
